/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, { createContext, FC, useContext, useReducer, useState } from 'react';
import {
    Basket,
    CommerceApiContext,
    Customer,
    CustomerProductList,
    CustomerProductListItem,
    CustomerProductListResult,
    IBasketContext,
    ICustomerContext,
} from '~/lib/data-contract/salesforce';
import { getApi } from '.';

/**
 * Provider and associated hook for accessing the Commerce API in React components.
 */
export const CommerceAPIContext = createContext<CommerceApiContext>({
    shopperCustomers: undefined,
    shopperStores: undefined,
    shopperBaskets: undefined,
    shopperProducts: undefined,
    auth: undefined,
    shopperLogin: undefined,
    shopperOrders: undefined,
    shopperPromotions: undefined,
    shopperSearch: undefined,
    shopperLookup: undefined,
    shopperGiftCertificates: undefined,
});
export const CommerceAPIProvider: FC = ({ children }) => {
    const api = getApi();
    return <CommerceAPIContext.Provider value={api}>{children}</CommerceAPIContext.Provider>;
};
export const useCommerceAPI = () => useContext<CommerceApiContext>(CommerceAPIContext);

/**
 * There are a few sources of global state in the react retail storefront.
 * Using React Context we implement a simple shared global state allowing
 * you can update and use either state from anywhere in the application.
 *
 * If your global state needs require a more robust solution, these contexts can be
 * replaced by a third party state management library of your choosing, such as MobX
 * or Redux.
 *
 * To use these context's simply import them into the component requiring context
 * like the below example:
 *
 * import React, {useContext} from 'react'
 * import {BasketContext} from 'components/_app-config'
 *
 * export const Avatar = () => {
 *    const {customer} = useContext(BasketContext)
 *    return <div>{customer.name}</div>
 * }
 *
 */

/************ Basket ************/
export const BasketContext = createContext<IBasketContext>({
    basket: undefined,
    setBasket: () => {},
});

export const BasketProvider: FC = ({ children }) => {
    const [basket, setBasket] = useState<Basket | undefined>(undefined);

    return (
        <BasketContext.Provider value={{ basket, setBasket }}>{children}</BasketContext.Provider>
    );
};

/************ Customer ************/
export const CustomerContext = createContext<ICustomerContext>({
    customer: undefined,
    setCustomer: () => {},
});

export const CustomerProvider: FC = ({ children }) => {
    const [customer, setCustomer] = useState<Customer | undefined>(undefined);

    return (
        <CustomerContext.Provider value={{ customer, setCustomer }}>
            {children}
        </CustomerContext.Provider>
    );
};

type actions = {
    receiveLists: (lists: CustomerProductListResult | undefined) => void;
    receiveList: (list: CustomerProductList | undefined) => void;
    createListItem: (listId: string, item: CustomerProductListItem) => void;
    updateListItem: (listId: string, item: CustomerProductListItem) => void;
    removeListItem: (listId: string, itemId: string) => void;
    reset: () => void;
};

export type CPL = {
    productLists: CustomerProductList[] | undefined;
};

export type CPLContext = {
    state: CPL;
    actions: actions | undefined;
};

/************ Customer Product Lists ************/
const CPLInitialValue = {
    // this is an object of product lists
    // keyed by list id, initial state
    // being undefined indicates the
    // product lists is not initialized
    productLists: undefined,
};

const CPLActionTypes = {
    RECEIVE_LISTS: 'RECEIVE_LISTS',
    RECEIVE_LIST: 'RECEIVE_LIST',
    CREATE_LIST_ITEM: 'CREATE_LIST_ITEM',
    UPDATE_LIST_ITEM: 'UPDATE_LIST_ITEM',
    REMOVE_LIST_ITEM: 'REMOVE_LIST_ITEM',
    RESET: 'RESET',
};

type RECEIVE_LIST_ACTION = {
    id: string;
};

type CREATE_LIST_ITEM_Action = {
    listId: number;
    item: CustomerProductListItem;
};

type REMOVE_LIST_ITEM_Action = {
    itemId: string;
    listId: number;
};

type Actions = {
    type: string;
    payload:
        | CustomerProductListResult
        | CustomerProductList
        | RECEIVE_LIST_ACTION
        | CREATE_LIST_ITEM_Action
        | REMOVE_LIST_ITEM_Action
        | undefined;
};

export const CustomerProductListsContext = createContext<CPLContext>({
    state: CPLInitialValue,
    actions: undefined,
});
export const CustomerProductListsProvider: React.FC = ({ children }) => {
    const [state, dispatch] = useReducer((state: CPL, { type, payload }: Actions) => {
        switch (type) {
            case CPLActionTypes.RECEIVE_LISTS: {
                const productLists = (payload as any[]).reduce((acc, curr) => {
                    return {
                        ...acc,
                        [curr.id]: curr,
                    };
                }, {});
                return { ...state, productLists };
            }
            case CPLActionTypes.RECEIVE_LIST: {
                const { id } = payload as RECEIVE_LIST_ACTION;
                // Tips: if you are unfamiliar with the concept of
                // reducers, keep in mind that reducers must be pure.
                // For an action like this, you must update every
                // level of nested data to avoid unexpected side effects
                return {
                    ...state,
                    productLists: {
                        ...state.productLists,
                        [id]: payload,
                    },
                };
            }
            case CPLActionTypes.CREATE_LIST_ITEM: {
                const { listId, item } = payload as CREATE_LIST_ITEM_Action;
                const { productId } = item;
                const items = state.productLists?.[listId]?.customerProductListItems || [];

                // if the item is already added to the list
                // we update the existing item
                const existingItemIndex = items.findIndex((i) => i.productId === productId);
                if (existingItemIndex >= 0) {
                    items[existingItemIndex] = item;
                } else {
                    items.push(item);
                }
                return {
                    ...state,
                    productLists: {
                        ...state.productLists,
                        [listId]: {
                            ...state.productLists?.[listId],
                            customerProductListItems: items,
                        },
                    },
                };
            }
            case CPLActionTypes.UPDATE_LIST_ITEM: {
                const { listId, item } = payload as CREATE_LIST_ITEM_Action;
                const items = state.productLists?.[listId]?.customerProductListItems?.map(
                    (listItem) => {
                        if (listItem.id === item.id) {
                            return {
                                ...listItem,
                                ...item,
                            };
                        }
                        return listItem;
                    },
                );
                return {
                    ...state,
                    productLists: {
                        ...state.productLists,
                        [listId]: {
                            ...state.productLists?.[listId],
                            customerProductListItems: items,
                        },
                    },
                };
            }
            case CPLActionTypes.REMOVE_LIST_ITEM: {
                const { listId, itemId } = payload as REMOVE_LIST_ITEM_Action;
                const productLists = {
                    ...state.productLists,
                };
                productLists[listId].customerProductListItems = productLists[
                    listId
                ].customerProductListItems?.filter((listItem) => {
                    return listItem.id !== itemId;
                });
                return { ...state, productLists };
            }
            case CPLActionTypes.RESET: {
                return { ...CPLInitialValue };
            }
            default:
                throw new Error('Unknown action.');
        }
    }, CPLInitialValue);

    const actions = {
        receiveLists: (lists: CustomerProductListResult | undefined) =>
            dispatch({ type: CPLActionTypes.RECEIVE_LISTS, payload: lists }),
        receiveList: (list: CustomerProductList | undefined) =>
            dispatch({ type: CPLActionTypes.RECEIVE_LIST, payload: list }),
        createListItem: (listId: string, item: CustomerProductListItem) =>
            dispatch({ type: CPLActionTypes.CREATE_LIST_ITEM, payload: { listId, item } }),
        updateListItem: (listId: string, item: CustomerProductListItem) =>
            dispatch({ type: CPLActionTypes.UPDATE_LIST_ITEM, payload: { listId, item } }),
        removeListItem: (listId: string, itemId: string) =>
            dispatch({ type: CPLActionTypes.REMOVE_LIST_ITEM, payload: { listId, itemId } }),
        reset: () => dispatch({ type: CPLActionTypes.RESET, payload: undefined }),
    };

    return (
        <CustomerProductListsContext.Provider value={{ state, actions }}>
            {children}
        </CustomerProductListsContext.Provider>
    );
};
